import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AiUsagePeriod,
  AiUsageSummary,
  AppConfig,
  GraphDataResponse,
  InspectionField,
  InspectionStandard,
  InspectionType,
  OrderStatus,
  Stats,
  SubProjectStatus
} from '@authority-labs/api-interfaces';
import { Path } from '@authority-labs/enums';
import { CitiesService } from '@check-list/app/modules/cities/cities.service';
import { CompanyStatusService } from '@check-list/app/modules/companies/company-status.service';
import { OrderStatusService } from '@check-list/app/modules/orders/order-status.service';
import { UsersService } from '@check-list/app/modules/users/users.service';
import { ChangeType } from '@check-list/core/enums';
import { forkJoin, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ColumnFilter } from '../../../classes/models/column-filter.model';
import { CrudService } from '../crud';

const API = environment.API;

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  orderStatus: OrderStatus[] = [];
  subProjectStatus: SubProjectStatus[] = [];
  inspectionTypes: InspectionType[] = [];
  fieldTypes: InspectionField[] = [];
  standards: InspectionStandard[] = [];
  roles: ColumnFilter[] = [];

  constructor(
    private http: HttpClient,
    private usersService: UsersService,
    private companyStatusService: CompanyStatusService,
    private orderStatusService: OrderStatusService,
    private citiesService: CitiesService
  ) {
    this.roles = [
      {
        label: 'מנהל',
        value: 'admin'
      },
      {
        label: 'מזכירות',
        value: 'office'
      },
      {
        label: 'בודק',
        value: 'tester'
      }
    ];

    CrudService.changesEmitter.subscribe((event) => {
      if (
        [ChangeType.deleted, ChangeType.edited, ChangeType.created].includes(
          event.action
        )
      ) {
        switch (event.path as Path) {
          case Path.InspectionFields:
          case Path.InspectionStandards:
          case Path.InspectionTypes:
          case Path.OrderStatus:
            this.getAllConfig().subscribe();
            break;
          default:
            break;
        }
      }
    });
  }

  loadAll() {
    return forkJoin([
      this.getAllConfig(),
      this.usersService.getAll(),
      this.companyStatusService.getAll(),
      this.orderStatusService.getAll(),
      this.citiesService.fetchAreas(),
      this.citiesService.getAll()
    ]);
  }

  getAllConfig(): Observable<AppConfig> {
    return this.http.get<AppConfig>(`${API}/configuration/all`).pipe(
      tap((resp) => {
        this.orderStatus = resp.orderStatus;
        this.subProjectStatus = resp.subProjectStatus;
        this.fieldTypes = resp.fieldTypes;
        this.standards = this.fieldTypes
          .map((field) => field.inspectionStandards)
          .flat();
        this.inspectionTypes = this.standards
          .map((standard) => standard.inspectionTypes)
          .flat();
      })
    );
  }

  getStats(): Observable<Stats> {
    return this.http.get<Stats>(`${API}/configuration/stats`);
  }

  getGraphData(path: Path, dateFrom: Date, dateTo?: Date): Observable<unknown> {
    let params: HttpParams = new HttpParams();
    params = this.getDateTSParam(dateFrom, 'dateFromTs', params);
    params = this.getDateTSParam(dateTo, 'dateToTs', params);
    return this.http
      .get<GraphDataResponse>(`${API}/configuration/graph/${path}`, { params })
      .pipe(
        map((res) => ({
          data: res.data.map((i) => ({
            name: new Date(i.name),
            value: i.value
          })),
          counter: res.counter
        }))
      );
  }

  getServerDateTime(): Observable<Date> {
    return this.http.get<Date>(`${API}/configuration/datetime`);
  }

  getAiUsageStats(period: AiUsagePeriod = 'month'): Observable<AiUsageSummary> {
    return this.http.get<AiUsageSummary>(`${API}/configuration/ai-usage`, {
      params: { period }
    });
  }

  getFiltarableInspectionTypes() {
    const inspectionTypes = new Set(
      this.inspectionTypes.map((i) => i.type ?? i.name)
    );
    return Array.from(inspectionTypes).sort();
  }

  private getDateTSParam(
    date: Date,
    name: string,
    params: HttpParams
  ): HttpParams {
    if (date) {
      params = params.set(name, String(date.getTime()));
    }
    return params;
  }
}
